import React from 'react'
import Video, { ReactPlayerProps, TrackProps } from 'react-player'
import { rgba } from 'polished'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Controller } from '../Controller'
import { styled } from '../../../styled'

import { usePlayer, Arguments as UsePlayerArguments } from './usePlayer'
import { PLAYBACK_RATE_OPTION, QUALITY_OPTION } from './constants'

type Props = UsePlayerArguments & {
  label?: string
  playerKey?: string
  poster?: string
  tracks?: TrackProps[]
}

const PlayerWrapper = styled.div<{ isFullScreen?: boolean }>`
  position: relative;
  line-height: 0;

  ${({ isFullScreen }) =>
    isFullScreen &&
    `
    width: 100%;
    height: 100%;
  `};
`

const VideoLabel = styled.span`
  font-size: ${({ theme }) => theme.sizes.font.s12};
  font-weight: bold;
  line-height: 1;
  position: absolute;
  top: 1em;
  left: 1em;
  display: inline-block;
  padding: 0.4em;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => rgba(theme.colors.black, 0.4)};
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ theme }) => theme.zIndexes.video.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => rgba(theme.colors.black, 0.3)};
`

const OverlayPlayIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 68px;
`

export const Player: React.FC<Props> = ({
  label,
  playerKey,
  poster,
  tracks,
  ...props
}) => {
  const {
    currentQuality,
    currentSource,
    currentTime,
    duration,
    hasChangedQuality,
    isFullscreen,
    isMuted,
    isPlaying,
    isShowingTracks,
    isUserIdle,
    onSubtitleClick,
    onContextMenu,
    onDuration,
    onEnded,
    onFullscreenChange,
    onMuteClick,
    onPlayClick,
    onPlaybackRateChange,
    onProgress,
    onQualityChange,
    onReady,
    onSeek,
    onStart,
    onVolumeChange,
    playbackRate,
    savedCurrentTime,
    videoRef,
    volume,
    wrapperRef,
  } = usePlayer({
    ...props,
  })

  const config: ReactPlayerProps['config'] = {
    file: {
      attributes: { poster },
      // tracksの値としてundefinedを渡せないため
      ...(tracks ? { tracks } : {}),
    },
  }

  return (
    <PlayerWrapper ref={wrapperRef} isFullScreen={isFullscreen}>
      {!isPlaying && (
        <Overlay onClick={onPlayClick}>
          {label && <VideoLabel>{label}</VideoLabel>}
          <OverlayPlayIcon icon="play-circle" />
        </Overlay>
      )}

      <Video
        config={config}
        controls={false}
        height="100%"
        key={playerKey}
        muted={isMuted}
        onClick={onPlayClick}
        onContextMenu={onContextMenu}
        onDuration={onDuration}
        onEnded={onEnded}
        onProgress={onProgress}
        onReady={() => onReady(savedCurrentTime)}
        onStart={() => onStart(hasChangedQuality)}
        playbackRate={Number(playbackRate.value)}
        playing={isPlaying}
        playsinline
        ref={videoRef}
        url={currentSource.src}
        volume={volume}
        width="100%"
      />

      <Controller
        currentQuality={currentQuality}
        currentTime={currentTime}
        duration={duration}
        isFullscreen={isFullscreen}
        isMuted={isMuted}
        isPlaying={isPlaying}
        isShowingTracks={isShowingTracks}
        isUserIdle={isUserIdle}
        onSubtitleClick={onSubtitleClick}
        onFullscreenChange={onFullscreenChange}
        onMuteClick={onMuteClick}
        onPlayClick={onPlayClick}
        onPlaybackRateChange={onPlaybackRateChange}
        onQualityChange={onQualityChange}
        onSeek={onSeek}
        onVolumeChange={onVolumeChange}
        playbackRate={playbackRate}
        playbackRateOption={PLAYBACK_RATE_OPTION}
        qualityOption={QUALITY_OPTION}
        tracks={tracks}
        volume={volume}
      />
    </PlayerWrapper>
  )
}
